import {gl_uniform, gl_variable} from '../../definitions/gl/variables.js';
import {getPositions, getDimensions, getRepeats, getCorner, getRotation, is3DType} from './types.js';
import {gl_sdf_scene, gl_sdf} from '../../definitions/gl/sdf/scene.js';
import {gl_vec3, gl_vec2} from '../../definitions/gl/vec.js';
import {gl_plus, gl_times} from '../../definitions/gl/index.js';

export default superclass => class extends superclass  {

    glDimensions = [];
    glPositions = [];
    glCorners = [];
    rotations = [];
    rotationUniforms = [];
    glRepeats = [];
    repeats = [];
    repeatsUniforms = [];
    glRotations = [];

    getTypeForId(id) {
        let obj = this.lastSDFs.find(x => x.id === id);
        if (!obj) {
            return undefined;
        }
        return obj[0];
    }

    updateUniformsForSDF(args, sdfList) {
        let index = this.getIndexForId(args.id);
        let _type = this.getTypeForId(args.copyId || args.id);

        if (isCopy(args)) {
            // then we just need to udate dimensions
            let stopRender = true;
            if (index === sdfList.length - 1) {
                stopRender = false;
            }
            this.updateUniformsForCopy(index, args, sdfList, stopRender);
            return;
        }
        let corner = getCorner(args);
        let rotation = getRotation(args);
        this.positions[index] = getPositions(args);
        this.rotations[index] = rotation;
        this.dimensions[index] = getDimensions(args);
        this.repeats[index] = getRepeats(args);
        this.corners[index] = corner;
        let uniformPosition = this.positionUniforms[index];
        let uniformRepeats = this.repeatsUniforms[index];
        let [uniformW, uniformH, uniformD] = this.dimensionsUniforms[index];
        let uniformC  = this.cornerUniforms[index];
        let uniformR = this.rotationUniforms[index];
        uniformPosition([this.positions[index]]);
        uniformW([this.dimensions[index][0]]);
        uniformH([this.dimensions[index][1]]);
        uniformRepeats([this.repeats[index]]);
        
        if (is3DType(args[0])) {
            let dimensionFactor = this.dimensionFactors[index];
            let depthFactor = 1;
            if (dimensionFactor) {
                depthFactor = dimensionFactor[2];
            }
            uniformD([this.dimensions[index][2]]);
        }
        if (args === sdfList[sdfList.length - 1]) {
            window.stopRender = false;
        }
        uniformC([corner]);
        uniformR([rotation]);
    }

    updateUniformsForCopy(index, args, sdfList, stopRender) {
        let {id, offset, copyId} = args;

        let type = this.getTypeForId(copyId);
        if (!is3DType(type)) {
            offset = offset.slice(0, 2);
        }

        this.positions[index] = offset;

        // this.positions[index] = getPositions(args);
        let uniformPos = this.positionUniforms[index];
        window.stopRender = stopRender;
        uniformPos([this.positions[index]]);
    }

    handleSDFCopy(index, args, sdfList) {
        let {id, offset, copyId} = args;

        // find the one we're copying
        let sdfToCopy, copyIndex;
        for (let i=0; i < sdfList.length; i++) {
            let sdf = sdfList[i];
            if (sdf.id === copyId) {
                copyIndex = i;
                sdfToCopy = sdf;
                break;
            }
        }

        if (!sdfToCopy) {
            return {sdf: undefined, sdfEvaluator: undefined};;
        }

        let dimensionsToCopy = this.glDimensions[copyIndex];
        let positionToCopy = this.glPositions[copyIndex];;
        let cornerToCopy = this.glCorners[copyIndex];
        let rotationToCopy = this.glRotations[copyIndex];

        let type = this.getTypeForId(copyId);
        let negativePositionToCopy = gl_times(this.zObject, () => positionToCopy)([type === "circle" ? -1 : 1]);

        let plus = gl_plus(this.zObject, () => negativePositionToCopy);

        // otherwise we use the dimension for this

        // POSITION + OFFSET

        // we need to put the offset in a uniform using getPosition then add it to the position we're
        // copying
        let argsToCopy = sdfToCopy;
        argsToCopy[1] = offset[0];
        argsToCopy[2] = offset[1];

        if (is3DType(type)) {
            argsToCopy[3] = offset[2];
        }


        let position = this.getPosition(index, argsToCopy);
        let positionExp = plus([position]);

        let sdf = this.glSDFs[index] || gl_sdf(
            this.zObject,
            () => sdfToCopy[0],
            () => dimensionsToCopy,
            () => cornerToCopy,
            () => {
                let ret= this.transformationMatrices[index] || this.transformationMatrix
                return ret;
            },

            () => rotationToCopy,
            () => index
        );

        this.glSDFs[index] = sdf;
        let [sdfEvaluator, sdfExpression] = sdf([positionExp]);

        let uniforms = sdfExpression.uniforms;
        this.indexedUniforms[index] = uniforms;

        return {sdf: sdfExpression, sdfEvaluator, uniforms: uniforms};
    }

    getPosition(index, args) {
        const position = getPositions(args);
        let [x,y,z] = position;
        // if 2 elements use vec2

        // for now just assume 2D and rect

        this.positions[index] = position;
        let posname = this.name('position', index);
        this.uniformValues[posname] = position;

        // need to keep an object of uniform values
        let uniformPosition  = this.positionUniforms[index] ||
            gl_uniform({}, () => posname);

        this.positionUniforms[index] = uniformPosition;
        this.glPositions[index] = uniformPosition([position], true);
        return this.glPositions[index];
    }

    getRepeats(index, args) {
        const repeats = getRepeats(args);
        // if 2 elements use vec2
        // for now just assume 2D and rect

        this.repeats[index] = repeats;
        let posname = this.name('repeats', index);
        this.uniformValues[posname] = repeats;

        // need to keep an object of uniform values
        let uniformRepeats = this.repeatsUniforms[index] ||
            gl_uniform({}, () => posname);

        this.repeatsUniforms[index] = uniformRepeats;
        this.glRepeats[index] = uniformRepeats([repeats], true);
        return this.glRepeats[index];
    }

    name(name, index) {
        let id = this.id.replaceAll('-', '');
        let y = name + index + id;
        return y;
    }

    getDimensions(index, args) {
        let dimensions = getDimensions(args);
        let [width, height, depth] = dimensions;
        let wname = this.name('dimWidth', index);
        let hname = this.name('dimHeight', index);
        let dname = this.name('dimDepth', index);
        this.dimensions[index] = dimensions;
        this.uniformValues[wname] = width;
        this.uniformValues[hname] = height;
        this.uniformValues[dname] = depth;
        let [uniformWidth, uniformHeight, uniformDepth] = this.dimensionsUniforms[index] ||
            [gl_uniform({}, () => wname),
             gl_uniform({}, () => hname),
             gl_uniform({}, () => dname),
            ];

        this.dimensionsUniforms[index] = [uniformWidth, uniformHeight, uniformDepth];

        let vec;
        if (this.dimensionVecs[index]) {
            vec = this.dimensionVecs[index];
        } else if (is3DType(args[0])) {
            vec = gl_vec3(this.zObject, () => uniformHeight([height], true), () => uniformDepth([depth], true));
        } else {
            vec = gl_vec2(this.zObject, () => uniformHeight([height], true));
        }
 
        this.dimensionVecs[index] = vec;
        this.glDimensions[index] = vec([uniformWidth([width], true)]);
        return this.glDimensions[index];
    }

    getCorner(index, corner) {
        let cname = this.name('corner', index);
        let uniformCorner = this.cornerUniforms[index] ||
            gl_uniform({}, () => cname);

        this.corners[index] = corner;
        this.cornerUniforms[index] = uniformCorner;

        this.glCorners[index] = uniformCorner([corner], true);
        return this.glCorners[index];
    }

    getRotation(index, r) {
        let rname = this.name('rotation', index);
        let uniformRotation = this.rotationUniforms[index] ||
            gl_uniform({}, () => rname);

        this.rotations[index] = r;
        this.rotationUniforms[index] = uniformRotation;

        this.glRotations[index] = uniformRotation([r], true);
        return this.glRotations[index];
    }

}

const isCopy = (x) => {
    return !Array.isArray(x);
};
